import type {
  UserAgencyMembership,
  UserContext,
  UserDepartmentMembership,
} from '@/lib/access-control/types';

export interface LicenseScope {
  agencyId: string | null;
  departmentId: string | null;
}

export interface RequestedLicenseScope {
  agencyId?: string | null;
  departmentId?: string | null;
}

export interface LicenseScopeAuth {
  agencies: Pick<UserAgencyMembership, 'agency_id'>[];
  departments: Pick<UserDepartmentMembership, 'department_id' | 'agency_id'>[];
  userContext: Pick<UserContext, 'active_agency_id' | 'active_department_id'>;
  canAccessTenant: (tenantId: string) => boolean;
  canAccessOrgUnit: (orgUnitId: string) => boolean;
}

function normalizeId(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function hasAgencyAccess(auth: LicenseScopeAuth, agencyId: string): boolean {
  return (
    auth.canAccessTenant(agencyId) ||
    auth.agencies.some((agency) => agency.agency_id === agencyId) ||
    auth.departments.some((department) => department.agency_id === agencyId)
  );
}

function hasDepartmentAccess(auth: LicenseScopeAuth, departmentId: string): boolean {
  return (
    auth.canAccessOrgUnit(departmentId) ||
    auth.departments.some((department) => department.department_id === departmentId)
  );
}

function getDepartment(
  auth: LicenseScopeAuth,
  departmentId: string
): Pick<UserDepartmentMembership, 'department_id' | 'agency_id'> | null {
  return auth.departments.find((department) => department.department_id === departmentId) || null;
}

function getPrimaryScope(auth: LicenseScopeAuth): LicenseScope {
  const primaryDepartment = auth.departments[0] || null;
  if (primaryDepartment) {
    return {
      agencyId: primaryDepartment.agency_id || auth.agencies[0]?.agency_id || null,
      departmentId: primaryDepartment.department_id,
    };
  }

  const primaryAgency = auth.agencies[0] || null;
  if (primaryAgency) {
    return {
      agencyId: primaryAgency.agency_id,
      departmentId: null,
    };
  }

  return {
    agencyId: null,
    departmentId: null,
  };
}

export function resolveLicenseScope(
  auth: LicenseScopeAuth,
  requested: RequestedLicenseScope = {}
): LicenseScope {
  const requestedDepartmentId = normalizeId(requested.departmentId);
  const activeDepartmentId = normalizeId(auth.userContext.active_department_id);

  for (const departmentId of [requestedDepartmentId, activeDepartmentId]) {
    if (!departmentId || !hasDepartmentAccess(auth, departmentId)) {
      continue;
    }

    const department = getDepartment(auth, departmentId);
    return {
      agencyId: department?.agency_id || null,
      departmentId,
    };
  }

  const requestedAgencyId = normalizeId(requested.agencyId);
  const activeAgencyId = normalizeId(auth.userContext.active_agency_id);

  for (const agencyId of [requestedAgencyId, activeAgencyId]) {
    if (!agencyId || !hasAgencyAccess(auth, agencyId)) {
      continue;
    }

    return {
      agencyId,
      departmentId: null,
    };
  }

  return getPrimaryScope(auth);
}

export function getLicenseCacheKey(userId: string, scope: LicenseScope): string {
  return [
    userId || 'anonymous',
    scope.agencyId || 'no-agency',
    scope.departmentId || 'no-department',
  ].join(':');
}
